import React, { useState } from 'react';
import { toast } from 'sonner';
import { Bell, Loader2, Eye, Send } from 'lucide-react';
import api from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

// Shared composer used by the Dashboard follow-ups and the event Guests tab.
// Lets the host add an optional personal note, preview the exact email, and
// send the reminder to the given recipients.
export const NudgeComposer = ({ eventId, eventTitle, recipients = [], open, onOpenChange, onSent }) => {
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html }
  const count = recipients.length;

  const close = (o) => {
    if (!o) { setNote(''); setPreview(null); }
    onOpenChange(o);
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/events/${eventId}/nudge/preview`, { note, emails: [] });
      setPreview(data);
    } catch (e) {
      toast.error('Could not load preview');
    } finally { setPreviewing(false); }
  };

  const send = async () => {
    if (count === 0) return;
    setSending(true);
    try {
      const { data } = await api.post(`/events/${eventId}/nudge`, { emails: recipients.map((r) => r.email), note });
      if (data.background) toast.success(`Sending reminders to ${data.queued} people in the background…`);
      else toast.success(`Reminder sent to ${data.sent} ${data.sent === 1 ? 'person' : 'people'}${data.skipped_unsubscribed ? ` · ${data.skipped_unsubscribed} skipped (unsubscribed)` : ''}`);
      onSent && onSent(recipients.map((r) => r.email));
      close(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send reminder');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="nudge-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Send a nudge</DialogTitle>
          <DialogDescription>
            A friendly reminder for &quot;{eventTitle}&quot; will be sent to {count} {count === 1 ? 'person' : 'people'}. Unsubscribed guests are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {count > 0 && count <= 6 && (
            <div className="flex flex-wrap gap-1.5" data-testid="nudge-recipients">
              {recipients.map((r) => (
                <Badge key={r.email} variant="secondary" className="font-normal">{r.name || r.email}</Badge>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="nudge-note">Add a personal note <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
              id="nudge-note"
              value={note}
              onChange={(e) => { setNote(e.target.value); }}
              placeholder="e.g. Would love to see you there — let me know if you can make it!"
              rows={3}
              data-testid="nudge-note-input"
            />
            <p className="text-xs text-muted-foreground">This appears in the email above the event details.</p>
          </div>

          {preview && (
            <div className="rounded-xl border border-border overflow-hidden" data-testid="nudge-preview">
              <div className="px-3 py-2 bg-secondary/50 text-xs border-b border-border">
                <span className="text-muted-foreground">Subject: </span><span className="font-medium text-foreground">{preview.subject}</span>
              </div>
              <iframe title="Nudge email preview" srcDoc={preview.html} className="w-full h-80 bg-white" sandbox="" />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="secondary" onClick={doPreview} disabled={previewing} className="gap-2" data-testid="nudge-preview-button">
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {preview ? 'Refresh preview' : 'Preview email'}
          </Button>
          <Button onClick={send} disabled={sending || count === 0} className="gap-2" data-testid="nudge-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send to {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
